import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon, PlusIcon } from '@radix-ui/react-icons';
import { Button, Card, Flex, Spinner, Box, Text, Tabs, Badge } from '@radix-ui/themes';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { EntitiesProjectType, EntityType, ExhibitorsProjectType, ProjectType, SubEntityType } from '../../../types/ScientificModule';
import { routes } from '../../../routes';
import TextFieldComponent from '../../../components/inputs/Textfield/TextfieldComponent';
import { useWatch,useFormContext  } from 'react-hook-form';
import { CreateProjectFormValidation } from './CreateProjectFormValidation';
import { getFile } from '../../../utils/fileUtils';
import { createProject } from '../../../services/scientific/project';

import DateFieldComponent from '../../../components/inputs/Datefield/DatefieldComponent';
import { DragAndDropFileUpload } from '../../../components/inputs/FileUpload/DragAndDropFileUpload';
import { useState,useEffect } from 'react';
import { useGetProjectTypesQuery } from '../../../hooks/scientific/useGetProjectTypesQuery';
import SelectComponent from '../../../components/inputs/Select/SelectComponent';
import TextAreaFieldComponent from '../../../components/inputs/TextAreafield/TextAreafieldComponent';
import { createProjectsDocument } from '../../../services/scientific/projectDocument';
import { useGetEntitiesQuery } from '../../../hooks/scientific/useGetEntitiesQuery';
import { useGetSubEntitiesByIdEntity } from '../../../hooks/scientific/useGetSubEntitiesQuery';

import { toDateOnly } from '../../../utils/dateFormatter';
import { getPhases } from '../../../services/scientific/phase';
import { createExhibitorsProject } from '../../../services/scientific/exhibitorProject';
import { createEntitiesProject } from '../../../services/scientific/entityProject';
import { createSubEntitiesProject } from '../../../services/scientific/subEntityProject';
import { useSelector } from 'react-redux';
import { useGetJudgeExhibitorByUserIdQuery } from '../../../hooks/scientific/useGetJudgeExhibitorByUserIdQuery';
import { RootState } from '../../../redux/store';
import MultiSelectComponent from "../../../components/inputs/Select/MultiSelectComponent";
import { PagedResultsType } from '../../../types/PagedResults';
import { EmptyState } from '../../../components/common/EmptyState';

type ActiveSegmentTab = 'Form' | 'Documents';

export const CreateProject = () => {
  const navigate = useNavigate();
  const mutation = useMutation({ mutationFn: createProject, mutationKey: 'createProject' });
  const mutationCreateProjectDocument = useMutation({ mutationFn: createProjectsDocument, mutationKey: 'createProjectsDocument' });
  const mutationCreateExhibitorsProjects = useMutation({ mutationFn: createExhibitorsProject, mutationKey: 'createExhibitorsProject' });
  const {mutateAsync: mutationCreateEntityProject, isLoading: isLoadingMutationCreateEntityProject} = useMutation({ mutationKey: "createEntitiesProject", mutationFn: createEntitiesProject });
  const {mutateAsync: mutationCreateSubEntityProject, isLoading: isLoadingMutationCreateSubEntityProject, } = useMutation({ mutationKey: "createSubEntitiesProject", mutationFn: createSubEntitiesProject });
  
  const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>('Form');
  const userLogged = useSelector((state: RootState) => state.login.userData);
  const { data:judgeOrExhibitor } = useGetJudgeExhibitorByUserIdQuery(userLogged.UserId, {
    enabled: !!userLogged.UserId
})

  const { data: projectTypes, isLoading:isLoadingProjectTypes, isError:isErrorProjectTypes, refetch:refreshProjectTypes } = useGetProjectTypesQuery({ PageNumber: 1, PageSize: 400});
  const { data: entities, isLoading:isLoadingEntities, isError:isErrorEntities, refetch:refreshEntities } = useGetEntitiesQuery({ PageNumber: 1, PageSize: 400});
  const loadingState = mutationCreateProjectDocument.isLoading || mutation.isLoading || mutationCreateExhibitorsProjects.isLoading;
  const defaultValues: ProjectType = {
    id: '',
    title: '',
    summary: '',
    linesOfResearch: '',
    targetAudience: '',
    expectedResults: '',
    finalConsiderations: '',
    description: '',
    startdate: '',
    enddate: '',
    entityName: '',
    idEntity: '',
    idProjectType: '',
    isFinanced: false,
    moneyInvested: 0,
    objectives: '',
    principalDocBase64: '',
    principalDocBase64File: undefined,
    status: true,
    documents: []
  };

  const { control, handleSubmit, setValue, getValues, formState: { errors }, trigger } = useForm<ProjectType>({
    resolver: yupResolver(CreateProjectFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });


 const selectedEntities = useWatch({ control, name: 'entityIds', defaultValue: [] });

  const { 
    data: subEntities,
    isLoading:isLoadingSubEntities, 
    isError:isErrorSubEntities, 
    refetch:refreshSubEntities 
  } = useGetSubEntitiesByIdEntity({ PageNumber: 1, PageSize: 400, IdEntity: selectedEntities?.[selectedEntities.length - 1]});
  
  const [validSubEntities, setValidSubEntities] = useState<SubEntityType[]>([]);


 useEffect(() => {
    if (subEntities?.items) {
      setValidSubEntities(prev => {
        const newItems = subEntities.items.filter(
          newItem => !prev.some(existingItem => existingItem.id === newItem.id)
        );
        return [...prev, ...newItems];
      });
    }
  }, [subEntities]);

  useEffect(() => {
    if (!selectedEntities?.length) {
      setValidSubEntities([]);
    }
  }, [selectedEntities]);

  
  const onSubmit = handleSubmit(async (data) => {
    try {
      data = {...data, 
        startdate: toDateOnly(new Date(data.startdate)),
        enddate:data.enddate? toDateOnly(new Date(data.enddate)):undefined,
      }
      data.principalDocBase64 = (await getFile(data.principalDocBase64File)).base64

      try{
        const {items} = await getPhases({PageSize:100, PageNumber: 1, IdProjectType: data.idProjectType})
        const startItem = items.filter(i=> i.idProjectType===data.idProjectType).find(i=> i.start);
        data.idCurrentPhase = startItem?.idPhaseNext;
      }
      catch(ex){
          console.error(ex)
      }
      
      const result = await mutation.mutateAsync(data);
      try {
        if(judgeOrExhibitor?.idExhibitor){
          const exhibitorProject : ExhibitorsProjectType ={
            idExhibitor: judgeOrExhibitor.idExhibitor,
            idProject: result,
            position: '',
            status: true
          }
          await mutationCreateExhibitorsProjects.mutateAsync(exhibitorProject)
        }
      } catch (error) {
        console.error(error)
      }

      if(data.documents && data.documents.length>0){
        const convertedToBase64Docs = await Promise.all(data.documents?.filter(d=> d.file).map(async (document) => {
          return getFile(document.file)
      }))
      
      await Promise.all(convertedToBase64Docs.map((cv) => {
          return mutationCreateProjectDocument.mutateAsync({
            docBase64: cv.base64,
            idProject: result
          })
      }))
      }

 
          if (data?.subEntityIds && data?.subEntityIds.length > 0) {
          for (let index = 0; index < data?.subEntityIds.length; index++) {
            const idSubEnt = data?.subEntityIds[index];
            await mutationCreateSubEntityProject({
              idSubEntity: idSubEnt,
              idProject: result,
                status: true
            })
          }
        }

        if (data?.entityIds && data?.entityIds.length > 0) {
          for (let index = 0; index < data?.entityIds.length; index++) {
            const idEnt = data?.entityIds[index];          
            await mutationCreateEntityProject({
              idEntity: idEnt,
              idProject: result,
                status: true
            })
          }
        }

       if (result) {
         toast.success('Projeto criado!');
         navigate(routes.projects);
       }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    navigate(routes.projects);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });
  const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
    if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
    trigger();
  }
  
  return (
    <form onSubmit={onSubmit} noValidate>
      <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate: string) => navigateAndValidate(tabToNavigate as ActiveSegmentTab)}>
        <Tabs.List>
          <Tabs.Trigger value="Form">
            Dados Principais <Badge color="red">{Object.keys(errors)?.length}</Badge>
          </Tabs.Trigger>
          <Tabs.Trigger value="Documents">Documentos</Tabs.Trigger>
        </Tabs.List>
      <Tabs.Content value="Form">
        <Card size='1'>
          <Flex direction='column' gap='4'>
              <TextAreaFieldComponent name="title" label="Título" control={control} errors={errors} fieldProps={{ placeholder: 'Título' }} />
              <TextAreaFieldComponent name="description" label="Descrição" control={control} errors={errors} fieldProps={{ placeholder: 'Descrição' }} required={true}/>
              <TextAreaFieldComponent name="summary" label="Sumário" control={control} errors={errors} fieldProps={{ placeholder: 'Sumário' }} />
              <TextAreaFieldComponent name="targetAudience" label="Público-Alvo" control={control} errors={errors} fieldProps={{ placeholder: 'Público-Alvo' }} />
              <TextAreaFieldComponent name="linesOfResearch" label="Linhas de Pesquisa" control={control} errors={errors} fieldProps={{ placeholder: 'Linhas de Pesquisa' }} />
             
              <SelectComponent
                name="idProjectType"
                label="Tipo de projecto"
                control={control}
                errors={errors}
                options={projectTypes?.items.map((projectType) => ({
                  value: projectType.id ?? "",
                  label: projectType.description ?? "",
                }))}
                itemsLoading={isLoadingProjectTypes}
                itemsError={isErrorProjectTypes}
                required={true}
              />
              <TextAreaFieldComponent name="expectedResults" label="Resultados Esperados" control={control} errors={errors} fieldProps={{ placeholder: 'Resultados Esperados' }} />
              <TextAreaFieldComponent name="finalConsiderations" label="Considerações Finais" control={control} errors={errors} fieldProps={{ placeholder: 'Considerações Finais' }} />
              <Flex gap='4'>
            <DateFieldComponent name="startdate" label="Data de Início" control={control} errors={errors} style={{width:'100%'}} required={true}/>
            <DateFieldComponent name="enddate" label="Data de Fim" control={control} errors={errors} style={{width:'100%'}} required={true}/>
              </Flex>
            <Flex gap='4'>
              <SelectComponent
                name="idEntity"
                label="Entidade (Sigla)"
                control={control}
                errors={errors}
                options={entities?.items.map((entity) => ({
                  value: entity.id ?? "",
                  label: entity.description ?? "",
                }))}
                style={{width:'100%'}} 
                itemsLoading={isLoadingEntities}
                itemsError={isErrorEntities}
                required={true}
              />
            <TextFieldComponent name="entityName" label="Entidade (Descrição Completa)" control={control} errors={errors} fieldProps={{ placeholder: 'Nome da Entidade' }} style={{width:'100%'}} />
              </Flex>

               <Flex  gap="6">
                          <MultiSelectComponent
                            name="entityIds"
                            label="Entidades"
                            control={control}
                            errors={errors}
                            style={{ width: "100%" }}
                             options={entities?.items.map((entity) => ({
                                          value: entity.id ?? "",
                                          label: entity.description ?? "",
                                        }))}
                                         itemsError={isErrorEntities}
                                        itemsLoading={isLoadingEntities}
                          />
                        </Flex>
                          <Flex  gap="6">
                          <MultiSelectComponent
                            name="subEntityIds"
                            label="SubEntidades"
                            control={control}
                            errors={errors}
                            style={{ width: "100%" }}
                             options={validSubEntities.map((subEntity) => ({
                                          value: subEntity.id ?? "",
                                          label: subEntity.description ?? "",
                                        }))}
                                         itemsError={isErrorSubEntities}
                                        itemsLoading={isLoadingSubEntities}
                          />
                        </Flex>
            <TextFieldComponent name="moneyInvested" label="Investimento" control={control} errors={errors} fieldProps={{ placeholder: 'Montante' }} />
            <TextAreaFieldComponent name="objectives" label="Objetivos" control={control} errors={errors} fieldProps={{ placeholder: 'Objetivos do projeto' }} required={true}/>

            <Box>
              <Text as="label" size="2"><span>Documento Principal <Text color="red">*</Text></span></Text>
              <Controller
                name="principalDocBase64File"
                control={control}
                render={({ field }) => (
                  <DragAndDropFileUpload
                    onFileDrop={(file) => {
                      field.onChange(file);
                    }}
                    label={`Arraste ou seleccione o ficheiro`}
                    accept="*"
                    selectedFile={field.value}
                  />
                )}
              />
              {errors.principalDocBase64File && (
                <Text color="red" size="1">{`${errors.principalDocBase64File.message}`}</Text>
              )}
            </Box>

            <Flex direction='row' gap='3' justify='end'>
              <Button type="button" variant='soft' color='gray' onClick={handleBack} disabled={loadingState}>
                <ChevronLeftIcon /> Voltar
              </Button>
              <Button type="submit" disabled={loadingState}>
                {loadingState ? <Spinner /> : <EnterIcon />}
                Registar
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Tabs.Content>
      <Tabs.Content value="Documents">
      <Card size="2">
          <Flex direction="column" gap="3">
            <Text as="label" weight="medium">Documentos</Text>
            {fields.length === 0 && (
              <EmptyState
                title="Sem documentos"
                description="Ainda não adicionou documentos de apoio a este projecto."
              />
            )}
            {fields.map((f, index) => (
              <Box
                key={index}
                p="3"
                style={{
                  border: '1px solid var(--gray-6)',
                  borderRadius: 'var(--radius-3)',
                  position: 'relative',
                }}
              >
                <Controller
                  name={`documents.${index}.file` as const}
                  control={control}
                  render={({ field }) => (
                    <DragAndDropFileUpload
                      onFileDrop={(file) => {
                        field.onChange(file);
                      }}
                      accept="*"
                      selectedFile={field.value}
                      remove={() => remove(index)}
                    />
                  )}
                />
                {errors.documents?.[index]?.file && (
                  <Text as="p" color="red" size="1" mt="1">
                    {`${errors.documents[index]?.message}`}
                  </Text>
                )}
              </Box>
            ))}
            <Button type="button" variant="soft" style={{ alignSelf: 'start' }} onClick={() => append(
              {
                file: null
               }
              )}>
            <PlusIcon /> Adicionar documento
            </Button>
          </Flex>
        </Card>
      </Tabs.Content>
      </Tabs.Root>
    </form>
  );
};
